import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { UpdateService } from 'src/app/core/services/update.service';
import { TelegramErrorReporterService } from 'src/app/core/services/telegram-error-reporter.service';
import { CommunityMembershipService } from 'src/app/core/services/community-membership.service';

/**
 * Backend Express fall-through for unknown routes responds
 * 404 { status_code: 404, status: false, message: 'Requested route not found', data: {} }
 * (route-service-provider.js → routeNotFound()). A stale cached bundle calling a
 * removed API route is the only in-app source of that exact message.
 */
const ROUTE_NOT_FOUND_PATTERN = /route not found/i;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(
    private logger: LoggerService,
    private updateService: UpdateService,
    private telegramReporter: TelegramErrorReporterService,
    private communityMembership: CommunityMembershipService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logger.debug('HTTP request failed', { url: req.url, method: req.method, status: error.status });

        // Remote /error-log API disabled

        // Only backend calls are alerted — IPFS gateways and third-party RPC hosts
        // fail often enough that they would drown the channel.
        if (this.isApiRequest(req.url)) {
          this.telegramReporter.report({
            type: 'HTTP',
            message: this.describe(error),
            name: error.name,
            url: req.urlWithParams,
            method: req.method,
            status: error.status
          });
        }

        // A community view the user is no longer in (moved or removed by the RealWorld team,
        // or the community was disabled): the page loads again in the right view. Safety net for
        // a missed USER_COMMUNITY_UPDATED socket event; the error still reaches the caller.
        if (this.communityMembership.isMembershipRefusal(req, error)) {
          this.communityMembership.membershipRefused();
        }

        if (this.isStaleClientSignal(req, error)) {
          // Old cached bundle calling a route the backend no longer serves.
          // Hard recovery is loop-guarded and processing-gated inside UpdateService.
          this.updateService.requestRecovery(`stale-route-404:${req.url}`);
        }

        return throwError(() => error);
      })
    );
  }

  // Backend errors carry the useful text in the JSON body; transport failures only in error.message.
  private describe(error: HttpErrorResponse): string {
    const body = error.error;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    const bodyMessage = body?.message;
    if (typeof bodyMessage === 'string' && bodyMessage.trim()) {
      return bodyMessage;
    }

    return error.message || `HTTP ${error.status}`;
  }

  private isStaleClientSignal(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    if (error.status !== 404 || !this.isApiRequest(req.url)) {
      return false;
    }
    const body = error.error;
    const message = typeof body === 'string' ? body : String(body?.message ?? '');
    return ROUTE_NOT_FOUND_PATTERN.test(message);
  }

  // Same base-URL membership pattern as RegulatedInterceptor.isBackendRequest (lines 40-47).
  private isApiRequest(url: string): boolean {
    return [
      environment.API_BASE_URL,
      environment.API_BASE_URL_V2,
      environment.API_BASE_URL_V3
    ].some((baseUrl) => url.startsWith(baseUrl));
  }
}
